import { useNavigate } from "react-router-dom";
import { Button, Panel, Typography, Skeleton, BooleanDisplay } from "@/components/atoms";
import { ConfigDetail } from "@/components/molecules";
import { usePermissions } from "@/permissions";
import { EPermission } from "@/permissions/types";
import { useGetDisplayOptionsQuery } from "@/redux/apis/config/business/displayOptions";
import { useAppSelector } from "@/redux/hooks";
import { displayOptionsSelector } from "@/redux/slices/business/selectors";
import { renderErrors } from "../../helpers";
import { DisplayOptionsEdit } from "./DisplayOptionsEdit";

export const DisplayOptions = () => {
  const { hasPermission } = usePermissions();
  const { isFetching } = useGetDisplayOptionsQuery();
  const { errors, details } = useAppSelector(displayOptionsSelector);
  const navigate = useNavigate();
  const canViewAdvanced = hasPermission(EPermission.ProviderConfigDisplayOptionsAdvancedManage);

  if (isFetching) return <Skeleton />;

  return (
    <Panel>
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <Typography variant="h3" className="leading-8">
            Display Options
          </Typography>
          <Typography className="text-neutral-dark-gray">Display the options that you wanted to see.</Typography>
        </div>
        {canViewAdvanced && (
          <div className="ml-auto">
            <Button startIcon="Edit2" onClick={() => navigate("edit")}>
              Edit
            </Button>
          </div>
        )}
      </div>
      {renderErrors(errors)}
      <ConfigDetail title="Booking Sheet" />
      <ConfigDetail
        title="Maximum Number of Stops"
        description="Configure the maximum number of additional stops a passenger can add to a booking"
        indentPadding="pl-2"
      >
        <Typography variant="paragraph">{details.bookingMaxNumberOfStops}</Typography>
      </ConfigDetail>

      {canViewAdvanced && (
        <>

          <ConfigDetail title="General Booking Display" />
          <ConfigDetail
            title="Booking Manager Display"
            description="Customize the view density of all booking entries."
            indentPadding="pl-2"
          >
            <Typography>
              {!details.enabledBookingHistoryUseOptimizedWideDisplay
                ? "Default - Standard view with enhanced row height for records spanning up to two lines, ensuring clear readability and detail visibility."
                : "Compact View - Compressed layout for a more streamlined view, aligning all records in a single line for a concise, space-efficient overview."}
            </Typography>
          </ConfigDetail>

          <ConfigDetail title="Dashboard Notes" />
          <ConfigDetail
            title="Enable 'Dashboard Note' field on booking and display on booking dashboard"
            description="Allows adding a unique note to each booking and displaying on the booking admin view."
            indentPadding="pl-2"
          >
            <BooleanDisplay status={details.enabledBookingManagerDashboardNoteFunctionality}>
              <Typography variant="paragraph">{details.enabledBookingManagerDashboardNoteFunctionality ? "Yes" : "No"}</Typography>
            </BooleanDisplay>
          </ConfigDetail>

          <ConfigDetail title="Invoicing Display" />
          <ConfigDetail
            title="Hide Pricing from Invoice Clients"
            description="When enabled, hides pricing information from clients associated with INVOICE-type companies"
            indentPadding="pl-2"
          >
            <BooleanDisplay status={details.pricingClientInvoiceDisplayDisabled}>
              <Typography variant="paragraph">{details.pricingClientInvoiceDisplayDisabled ? "Yes" : "No"}</Typography>
            </BooleanDisplay>
          </ConfigDetail>
          <ConfigDetail
            title="Disable Invoice & Credit Card Mixing"
            description="When enabled, prevents having both invoicing and credit card payment methods active"
            indentPadding="pl-2"
          >
            <BooleanDisplay status={details.pricingInvoiceCreditCardMixingDisabled}>
              <Typography variant="paragraph">{details.pricingInvoiceCreditCardMixingDisabled ? "Yes" : "No"}</Typography>
            </BooleanDisplay>
          </ConfigDetail>
        </>
      )}
    </Panel>
  );
};

DisplayOptions.Edit = DisplayOptionsEdit;
